import React, { useState, useEffect, useCallback } from 'react';
import { CalendarIcon, ClockIcon, UserIcon, BookOpenIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { Link } from 'react-router-dom';

const SKILL_COLORS = { grammar:'#1D4ED8', vocabulary:'#16A34A', writing:'#C2410C', reading:'#7C3AED', speaking:'#92400E' };
const SKILL_BG    = { grammar:'#EFF6FF', vocabulary:'#F0FDF4', writing:'#FFF7ED', reading:'#F5F3FF', speaking:'#FEF9C3' };

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' });
}
const formatTime = h => String(h).padStart(2,'0') + ':00';

function MaterialCard({ mat }) {
  const [open, setOpen] = useState(false);
  const content = mat.content || {};
  const sc = SKILL_COLORS[mat.skill_type] || '#374151';
  const sb = SKILL_BG[mat.skill_type]    || '#F3F4F6';
  return (
    <div style={{ border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden', marginBottom:8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background: open ? sb : '#FAFAFA', cursor:'pointer' }}>
        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:sb, color:sc }}>{mat.skill_type}</span>
        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#0D1B2A', color:'#C9A84C', letterSpacing:1 }}>{mat.cefr_level}</span>
        <span style={{ flex:1, fontSize:13, fontWeight:700, color:'#0D1B2A' }}>{mat.title}</span>
        {mat.theme && <span style={{ fontSize:11, color:'#6B7280' }}>{mat.theme}</span>}
        <Link
          to={`/tutor/lesson/${mat.id || mat.material_id}/present`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', background:'#0D1B2A', color:'#C9A84C', borderRadius:6, fontSize:11, fontWeight:700, textDecoration:'none', flexShrink:0, whiteSpace:'nowrap' }}>
          🖥️ Present
        </Link>
        {open ? <ChevronUpIcon style={{ width:16, height:16, color:'#6B7280' }} /> : <ChevronDownIcon style={{ width:16, height:16, color:'#6B7280' }} />}
      </div>
      {open && (
        <div style={{ padding:'14px 16px', background:'#fff', borderTop:'1px solid #F3F4F6' }}>
          {mat.description && <p style={{ margin:'0 0 12px', fontSize:12, color:'#6B7280', lineHeight:1.5 }}>{mat.description}</p>}

          {mat.file_url && (
            <div style={{ display:'flex', alignItems:'center', gap:10, background:'#F0F7FA', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
              <span style={{ fontSize:20 }}>{mat.file_type === 'pdf' ? String.fromCodePoint(0x1F4C4) : mat.file_type && mat.file_type.includes('ppt') ? String.fromCodePoint(0x1F4CA) : String.fromCodePoint(0x1F4DD)}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A' }}>Lesson file attached</div>
                <div style={{ fontSize:11, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{mat.file_name}</div>
              </div>
              <a href={mat.file_url} target="_blank" rel="noopener noreferrer"
                style={{ padding:'6px 14px', background:'#00B4D8', color:'#fff', borderRadius:7, fontWeight:700, fontSize:12, textDecoration:'none', whiteSpace:'nowrap' }}>
                Open file
              </a>
            </div>
          )}

          {content.explanation && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A', marginBottom:5 }}>📖 Explanation</div>
              <div style={{ fontSize:13, color:'#374151', background:sb, borderRadius:8, padding:'10px 12px', lineHeight:1.6 }}>{content.explanation}</div>
            </div>
          )}
          {content.examples && content.examples.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A', marginBottom:5 }}>✏️ Examples</div>
              <ul style={{ margin:0, paddingLeft:18 }}>
                {content.examples.map((ex, i) => <li key={i} style={{ fontSize:13, color:'#374151', marginBottom:3, lineHeight:1.5 }}>{ex}</li>)}
              </ul>
            </div>
          )}
          {content.words && content.words.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A', marginBottom:6 }}>📚 Words ({content.words.length})</div>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {content.words.map((w, i) => (
                  <div key={i} style={{ background:'#F9FAFB', borderRadius:7, padding:'6px 10px' }}>
                    <span style={{ fontWeight:700, fontSize:13 }}>{w.word}</span>
                    <span style={{ fontSize:12, color:'#6B7280', marginLeft:8 }}>— {w.definition}</span>
                    {w.example && <div style={{ fontSize:11, color:'#9CA3AF', fontStyle:'italic', marginTop:2 }}>"{w.example}"</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {content.prompt && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A', marginBottom:5 }}>✍️ Writing Prompt</div>
              <div style={{ fontSize:13, color:'#374151', background:'#FFF7ED', borderRadius:8, padding:'10px 12px', lineHeight:1.6 }}>{content.prompt}</div>
              {content.requirements && <ul style={{ margin:'6px 0 0', paddingLeft:18 }}>{content.requirements.map((r,i) => <li key={i} style={{ fontSize:12, color:'#6B7280', marginBottom:2 }}>{r}</li>)}</ul>}
            </div>
          )}
          {content.passage && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A', marginBottom:5 }}>📄 Passage</div>
              <div style={{ fontSize:13, color:'#374151', background:'#F5F3FF', borderRadius:8, padding:'10px 12px', lineHeight:1.7 }}>{content.passage}</div>
            </div>
          )}
          {content.exercises && content.exercises.length > 0 && (
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B2A', marginBottom:6 }}>🧩 Exercises ({content.exercises.length})</div>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {content.exercises.slice(0,5).map((ex, i) => (
                  <div key={i} style={{ background:'#F9FAFB', borderRadius:7, padding:'7px 10px', fontSize:12 }}>
                    <span style={{ fontWeight:600 }}>{i+1}. </span>{ex.question}
                    {ex.answer && <span style={{ color:'#10B981', marginLeft:8, fontWeight:600 }}>✓ {ex.answer}</span>}
                  </div>
                ))}
                {content.exercises.length > 5 && <div style={{ fontSize:11, color:'#9CA3AF', textAlign:'center' }}>+{content.exercises.length-5} more</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BookingRow({ b, isUpcoming }) {
  const [expanded, setExpanded] = useState(false);
  const [materials, setMaterials] = useState(null); // null = not loaded
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!expanded && materials === null) {
      setLoading(true);
      try {
        const r = await api.get(`/api/tutor/bookings/${b.id}/assigned-materials`).catch(() => null);
        setMaterials(r?.data?.materials || []);
      } catch { setMaterials([]); }
      setLoading(false);
    }
    setExpanded(o => !o);
  };

  const hasMaterials = materials && materials.length > 0;

  return (
    <div style={{ borderRadius:12, border: isUpcoming ? '1px solid #DDD6FE' : '1px solid #E5E7EB', overflow:'hidden', background: isUpcoming ? '#F5F3FF' : '#F9FAFB', opacity: isUpcoming ? 1 : 0.7 }}>
      {/* Main row */}
      <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px' }}>
        <div style={{ width:44, height:44, borderRadius:12, background:(isUpcoming ? '#7C3AED' : '#6B7280') + '20', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          {isUpcoming ? <UserIcon style={{ width:20, height:20, color:'#7C3AED' }} /> : <ClockIcon style={{ width:20, height:20, color:'#6B7280' }} />}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:14, color:'#1F2937' }}>{b.student_name}</div>
          <div style={{ fontSize:13, color:'#6B7280', marginTop:2 }}>
            {formatDate(b.booking_date)} · {formatTime(b.time_slot)} – {formatTime(b.time_slot + 1)}
          </div>
        </div>
        {isUpcoming && (
          <button onClick={toggle} style={{
            display:'flex', alignItems:'center', gap:6, padding:'6px 14px',
            background: expanded ? '#7C3AED' : '#EDE9FE',
            color: expanded ? '#fff' : '#7C3AED',
            border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700, flexShrink:0,
          }}>
            <BookOpenIcon style={{ width:14, height:14 }} />
            {loading ? 'Loading…' : expanded ? 'Hide Lesson' : '📚 View Lesson'}
          </button>
        )}
        <span style={{ fontSize:12, fontWeight:600, padding:'4px 12px', borderRadius:20, flexShrink:0,
          background: isUpcoming ? '#EDE9FE' : '#F3F4F6',
          color: isUpcoming ? '#7C3AED' : '#6B7280' }}>
          {isUpcoming ? (b.status === 'confirmed' ? 'Confirmed' : 'Pending') : 'Past'}
        </span>
      </div>

      {/* Lesson panel */}
      {expanded && isUpcoming && (
        <div style={{ borderTop:'1px solid #DDD6FE', padding:'16px 18px', background:'#fff' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:16, color:'#6B7280', fontSize:13 }}>Loading lesson materials…</div>
          ) : hasMaterials ? (
            <>
              <div style={{ fontSize:12, fontWeight:700, color:'#6B7280', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>
                📚 Assigned Lesson Materials — click any to expand
              </div>
              {materials.map(mat => <MaterialCard key={mat.id || mat.material_id} mat={mat} />)}
            </>
          ) : (
            <div style={{ textAlign:'center', padding:'20px 16px' }}>
              <div style={{ fontSize:28, marginBottom:8 }}>📭</div>
              <div style={{ fontSize:13, fontWeight:600, color:'#0D1B2A', marginBottom:4 }}>No lesson assigned yet</div>
              <div style={{ fontSize:12, color:'#6B7280' }}>Ask your admin to assign lesson materials for this session.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const C = { teal:'#00B4D8', success:'#10B981', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F9FAFB', navy:'#0D1B2A', booked:'#7C3AED', warning:'#F59E0B' };
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function CalendarDay({ date, isToday, isSelected, hasSlots, hasBookings, onClick }) {
  return (
    <div onClick={onClick} style={{
      width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:13, fontWeight: isToday ? 700 : 400,
      background: isSelected ? C.teal : isToday ? '#E0F7FB' : 'transparent',
      color: isSelected ? '#fff' : isToday ? C.teal : C.dark,
      cursor:'pointer', position:'relative',
      border: isSelected ? '2px solid ' + C.teal : '2px solid transparent',
    }}>
      {date.getDate()}
      {(hasSlots || hasBookings) && !isSelected && (
        <span style={{ position:'absolute', bottom:3, left:'50%', transform:'translateX(-50%)', width:4, height:4, borderRadius:'50%', background: hasBookings ? C.booked : C.success }} />
      )}
    </div>
  );
}

export default function TutorSchedule() {
  const [schedule, setSchedule] = useState({});
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calDate, setCalDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const fetchSchedule = useCallback((year, month) => {
    setLoading(true);
    const start = year + '-' + String(month + 1).padStart(2,'0') + '-01';
    const lastDay = new Date(year, month + 1, 0).getDate();
    const end = year + '-' + String(month + 1).padStart(2,'0') + '-' + lastDay;
    api.get('/api/tutor/schedule?start=' + start + '&end=' + end)
      .then(r => setSchedule(r.data.schedule || {}))
      .catch(() => setSchedule({}))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSchedule(calDate.getFullYear(), calDate.getMonth()); }, [calDate, fetchSchedule]);

  useEffect(() => {
    api.get('/api/tutor/bookings').then(r => setBookings(r.data.bookings || [])).catch(() => {});
  }, []);

  const today = new Date();
  const year = calDate.getFullYear();
  const month = calDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const getDateKey = (d) => d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  const getSlotsForDate = (d) => schedule[getDateKey(d)] || [];
  const hasBookingsOnDate = (d) => getSlotsForDate(d).some(s => s.status === 'booked');
  const hasSlotsOnDate = (d) => getSlotsForDate(d).length > 0;
  const selectedSlots = getSlotsForDate(selectedDate);
  const isToday = (d) => d.toDateString() === today.toDateString();
  const isSelected = (d) => d.toDateString() === selectedDate.toDateString();

  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(new Date(year, month, d));

  const bookedCount = selectedSlots.filter(s => s.status === 'booked').length;
  const availableCount = selectedSlots.filter(s => s.status === 'available').length;

  const todayStr = getDateKey(today);
  const upcomingBookings = bookings.filter(b => b.booking_date >= todayStr);
  const recentBookings = bookings.filter(b => b.booking_date < todayStr);

  return (
    <Layout role="tutor">
      <div style={{ maxWidth:900 }}>

        {/* Booked Lessons Section */}
        {bookings.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:C.navy }}>
                My Booked Lessons ({bookings.length})
              </h3>
              <span style={{ fontSize:12, color:C.grey, background:'#F0F7FA', padding:'3px 10px', borderRadius:20 }}>
                Click "📚 View Lesson" to preview assigned materials before teaching
              </span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {upcomingBookings.map(b => <BookingRow key={b.id} b={b} isUpcoming={true} />)}
              {recentBookings.map(b => <BookingRow key={b.id} b={b} isUpcoming={false} />)}
            </div>
          </div>
        )}

        {/* Calendar + Day View */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap:24 }}>
          <div className="card" style={{ padding:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <button onClick={() => setCalDate(new Date(year, month-1, 1))} style={{ background:'none', border:'none', cursor:'pointer', padding:6, borderRadius:6, color:C.grey, fontSize:18 }}>&#8249;</button>
              <span style={{ fontWeight:700, fontSize:15, color:C.navy }}>{MONTHS[month]} {year}</span>
              <button onClick={() => setCalDate(new Date(year, month+1, 1))} style={{ background:'none', border:'none', cursor:'pointer', padding:6, borderRadius:6, color:C.grey, fontSize:18 }}>&#8250;</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:8 }}>
              {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:C.grey, padding:'4px 0' }}>{d}</div>)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
              {calDays.map((d, i) => d ? (
                <CalendarDay key={i} date={d} isToday={isToday(d)} isSelected={isSelected(d)} hasSlots={hasSlotsOnDate(d)} hasBookings={hasBookingsOnDate(d)} onClick={() => setSelectedDate(d)} />
              ) : <div key={i} />)}
            </div>
            <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:6, fontSize:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, color:C.grey }}><span style={{ width:8, height:8, borderRadius:'50%', background:C.success, display:'inline-block' }} /> Available slots</div>
              <div style={{ display:'flex', alignItems:'center', gap:6, color:C.grey }}><span style={{ width:8, height:8, borderRadius:'50%', background:C.booked, display:'inline-block' }} /> Has bookings</div>
            </div>
          </div>

          <div className="card" style={{ padding:24 }}>
            <div style={{ marginBottom:20 }}>
              <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:700, color:C.navy }}>
                {selectedDate.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' })}
              </h3>
              <span style={{ fontSize:13, color:C.grey }}>
                {selectedSlots.length === 0 ? 'No availability set for this day'
                  : (bookedCount > 0 ? bookedCount + ' booked · ' : '') + availableCount + ' available'}
              </span>
            </div>
            {loading ? (
              <div style={{ textAlign:'center', padding:40, color:C.grey }}>Loading...</div>
            ) : selectedSlots.length > 0 ? (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {selectedSlots.map((slot, i) => {
                  const isBooked = slot.status === 'booked';
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background: isBooked ? '#F5F3FF' : C.bg, borderRadius:10, border:'1px solid ' + (isBooked ? '#DDD6FE' : C.border) }}>
                      <div style={{ width:40, height:40, borderRadius:10, background: (isBooked ? C.booked : C.teal) + '20', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {isBooked ? <UserIcon style={{ width:18, height:18, color:C.booked }} /> : <ClockIcon style={{ width:18, height:18, color:C.teal }} />}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:14, color:C.dark }}>{formatTime(slot.hour)} {'\u2013'} {formatTime(slot.hour+1)}</div>
                        {isBooked ? <div style={{ fontSize:12, color:C.booked, fontWeight:500 }}>{slot.student_name}</div>
                          : <div style={{ fontSize:12, color:C.grey }}>1 hour slot</div>}
                      </div>
                      <span style={{ fontSize:12, fontWeight:600, padding:'4px 10px', borderRadius:20, color: isBooked ? C.booked : C.success, background: isBooked ? '#EDE9FE' : '#D1FAE5' }}>
                        {isBooked ? 'Booked' : 'Available'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign:'center', padding:48 }}>
                <CalendarIcon style={{ width:48, height:48, color:C.border, margin:'0 auto 16px', display:'block' }} />
                <h4 style={{ margin:'0 0 8px', color:C.dark }}>No slots for this day</h4>
                <Link to="/tutor/availability" style={{ display:'inline-block', padding:'10px 20px', background:C.teal, color:'#fff', borderRadius:8, textDecoration:'none', fontWeight:600, fontSize:14 }}>Set Availability</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
